/**
 * Lead-room + info-dealer actions (Sprint 3a).
 *  - VIP_ROOM_ENTER : unlock a VIP lead room for a while (dispatched AFTER a
 *    rewarded ad; UI owns the ad flow + adHookStats logging).
 *  - BUY_INFO       : spend GAME MONEY on a one-shot, deliberately-vague hint
 *    from the back-alley dealer (whale hint / next-news coin / room band).
 */
import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import { getCurrentCPS } from './state.js';
import { getLeadRoom, LEAD_ROOMS, LEAD_ROOM_CONFIG } from './config/leadRooms.js';
import { getInfoProduct, infoProductCost, accuracyBand } from './config/infoShop.js';
import {
  CHATTER_CONFIG, ECHO_POOL_BY_MOOD, ECHO_POOL_SIZE, getReaction,
} from './config/roomChatter.js';
import { NPC_NICKS } from './config/comments.js';
import type { ChatMsg, InfoHint, RoomRuntime } from './leadRoomState.js';

export type LeadRoomAction =
  | { type: 'VIP_ROOM_ENTER'; payload: { roomId: string } }
  | { type: 'BUY_INFO'; payload: { productId: string } }
  | { type: 'RUMOR_FACTCHECK'; payload: { roomId: string; rumorId: string } }
  | { type: 'POST_ROOM_REACTION'; payload: { roomId: string; reactionIdx: number } };

/** Unlock a VIP room (ad reward). No-op for non-VIP or unknown rooms. */
export const handleVipRoomEnter: ActionHandler<
  GameState,
  Extract<LeadRoomAction, { type: 'VIP_ROOM_ENTER' }>
> = (state, action) => {
  if (!state.leadRooms) return null;
  const cfg = getLeadRoom(action.payload.roomId);
  if (!cfg || !cfg.vip) return null;
  const room = state.leadRooms.rooms[cfg.id];
  if (!room) return null;

  const now = Date.now();
  return {
    ...state,
    leadRooms: {
      ...state.leadRooms,
      rooms: {
        ...state.leadRooms.rooms,
        [cfg.id]: { ...room, unlockedUntil: now + LEAD_ROOM_CONFIG.vipUnlockDuration },
      },
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

/** Pick the most "interesting" whale coin to hint at (accumulating/pumping first). */
function pickWhaleHintCoin(state: GameState): string | undefined {
  const whales = state.whales ?? {};
  const active = Object.entries(whales).filter(
    ([, w]) => w.phase === 'accumulating' || w.phase === 'pumping'
  );
  const pool = active.length > 0 ? active : Object.entries(whales);
  if (pool.length === 0) return undefined;
  return pool[Math.floor(Math.random() * pool.length)][0];
}

/** Build the revealed hint payload for a product (deliberately partial). */
function buildHint(state: GameState, productId: string, now: number): InfoHint | null {
  const product = getInfoProduct(productId);
  if (!product) return null;
  const base = { productId: product.id, boughtAt: now, expiresAt: now + product.hintTtl };

  if (product.id === 'whale_hint') {
    const coinId = pickWhaleHintCoin(state);
    if (!coinId) return null;
    return { ...base, coinId };
  }
  if (product.id === 'news_tip') {
    const coinId = state.info?.pendingNews?.coinId;
    if (!coinId) return null; // nothing brewing - refuse the sale
    return { ...base, coinId };
  }
  // room_leak: expose one room's reliability band (not the exact number)
  const rooms = LEAD_ROOMS;
  const cfg = rooms[Math.floor(Math.random() * rooms.length)];
  return { ...base, roomId: cfg.id, band: accuracyBand(cfg.accuracy) };
}

/**
 * Buy a one-shot info hint for GAME MONEY. Price scales with CPS (expensive by
 * design). Refuses the sale if there is no meaningful hint to give right now
 * (so the player isn't charged for nothing).
 */
export const handleBuyInfo: ActionHandler<
  GameState,
  Extract<LeadRoomAction, { type: 'BUY_INFO' }>
> = (state, action) => {
  if (!state.leadRooms) return null;
  const product = getInfoProduct(action.payload.productId);
  if (!product) return null;

  const cost = infoProductCost(product, getCurrentCPS(state));
  if (state.coins < cost) return null;

  const now = Date.now();
  const hint = buildHint(state, product.id, now);
  if (!hint) return null; // no info available -> no charge

  return {
    ...state,
    coins: state.coins - cost,
    leadRooms: {
      ...state.leadRooms,
      hints: [...state.leadRooms.hints.filter(h => h.productId !== product.id), hint],
    },
  };
};

/**
 * Fact-check a live rumor (ad reward): flips its verdict to visible. If genuine,
 * the chatter system fires its reserved price move on the next tick. No-op for
 * unknown / already-resolved rumors.
 */
export const handleRumorFactcheck: ActionHandler<
  GameState,
  Extract<LeadRoomAction, { type: 'RUMOR_FACTCHECK' }>
> = (state, action) => {
  if (!state.leadRooms) return null;
  const { roomId, rumorId } = action.payload;
  const room = state.leadRooms.rooms[roomId];
  if (!room) return null;
  const rumor = room.rumors.find(r => r.id === rumorId);
  if (!rumor || rumor.factChecked) return null;

  return {
    ...state,
    leadRooms: {
      ...state.leadRooms,
      rooms: {
        ...state.leadRooms.rooms,
        [roomId]: {
          ...room,
          rumors: room.rumors.map(r => r.id === rumorId ? { ...r, factChecked: true } : r),
        },
      },
    },
    statistics: { ...state.statistics, adsWatched: state.statistics.adsWatched + 1 },
  };
};

/**
 * Build the player's reaction line + 1~2 NPC echo lines (mood-matched).
 * Pure aside from Math.random - exported for tests.
 */
export function buildReaction(reactionIdx: number, now: number): ChatMsg[] | null {
  const reaction = getReaction(reactionIdx);
  if (!reaction) return null;
  const mine: ChatMsg = {
    id: `me_${now}`,
    nickIdx: -1,
    category: 'echo',
    textIdx: 0,
    mood: reaction.mood,
    reactionKey: reaction.key,
    at: now,
  };
  const echoCount = CHATTER_CONFIG.reactionEchoMin
    + Math.floor(Math.random() * (CHATTER_CONFIG.reactionEchoMax - CHATTER_CONFIG.reactionEchoMin + 1));
  const echoes: ChatMsg[] = [];
  for (let i = 0; i < echoCount; i++) {
    const at = now + CHATTER_CONFIG.reactionEchoDelayMin
      + Math.random() * (CHATTER_CONFIG.reactionEchoDelayMax - CHATTER_CONFIG.reactionEchoDelayMin);
    echoes.push({
      id: `echo_${now}_${i}`,
      nickIdx: Math.floor(Math.random() * NPC_NICKS.length),
      category: 'echo',
      textIdx: Math.floor(Math.random() * ECHO_POOL_SIZE),
      mood: reaction.mood,
      at,
    });
  }
  return [mine, ...echoes];
}

function appendChat(room: RoomRuntime, msgs: ChatMsg[]): RoomRuntime {
  return { ...room, chat: [...room.chat, ...msgs].slice(-CHATTER_CONFIG.maxChat) };
}

/**
 * Player taps a reaction (가즈아 / 손절함 / 관망). Posts the line and queues
 * NPC echoes a couple seconds out (their `at` is in the future; the view renders
 * only messages whose `at <= now`). Pure flavor - no market impact.
 */
export const handlePostRoomReaction: ActionHandler<
  GameState,
  Extract<LeadRoomAction, { type: 'POST_ROOM_REACTION' }>
> = (state, action) => {
  if (!state.leadRooms) return null;
  const { roomId, reactionIdx } = action.payload;
  const room = state.leadRooms.rooms[roomId];
  if (!room) return null;

  const msgs = buildReaction(reactionIdx, Date.now());
  if (!msgs) return null;

  return {
    ...state,
    leadRooms: {
      ...state.leadRooms,
      rooms: { ...state.leadRooms.rooms, [roomId]: appendChat(room, msgs) },
    },
  };
};

export { ECHO_POOL_BY_MOOD };

export const leadRoomActionHandlers = {
  VIP_ROOM_ENTER: handleVipRoomEnter,
  BUY_INFO: handleBuyInfo,
  RUMOR_FACTCHECK: handleRumorFactcheck,
  POST_ROOM_REACTION: handlePostRoomReaction,
} as const;
